import { ImageIcon, Upload } from 'lucide-react';
import { useState } from 'react';
import { useWatch } from 'react-hook-form';
import type { Control } from 'react-hook-form';

import type { Performance } from '~entities/recital-manager/model/recital';

import { Button } from '~shared/shadcn/ui/button';
import { Card, CardContent } from '~shared/shadcn/ui/card';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~shared/shadcn/ui/form';
import { Input } from '~shared/shadcn/ui/input';
import { Switch } from '~shared/shadcn/ui/switch';
import { Textarea } from '~shared/shadcn/ui/textarea';

// Importing the type from basic-info-tab to maintain consistency
import type { PerformanceFormValues } from './edit-performance-sheet';
import { StudentSizesSection } from './student-sizes-section';
import { UploadCostumeImage } from './upload-costume-image';

interface CostumeDetailsTabProps {
  control: Control<PerformanceFormValues>;
  actId: string;
  performance: Performance;
}

export const CostumeDetailsTab = ({
  control,
  actId,
  performance,
}: CostumeDetailsTabProps) => {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  // Watch the propsNeeded field to conditionally render the description field
  const propsNeeded = useWatch({
    control,
    name: 'propsNeeded',
    defaultValue: false,
  });

  // Get costume image URL directly from the performance data
  const costumeImageUrl = performance.costume?.imageUrl ?? '';

  return (
    <div className='space-y-4'>
      {/* Costume Image Preview Section */}
      <Card className='overflow-hidden rounded-lg border bg-card'>
        <CardContent className='p-4'>
          <div className='mb-3 flex items-center gap-2'>
            <div className='flex size-6 items-center justify-center rounded-full bg-yellow-300 text-yellow-800'>
              <span className='text-sm'>i</span>
            </div>
            <h3 className='text-lg font-semibold'>Preview</h3>
          </div>

          <div className='flex flex-col items-center'>
            {costumeImageUrl ? (
              <div className='relative mb-3 h-48 w-full overflow-hidden rounded-md'>
                <img
                  src={costumeImageUrl}
                  alt='Costume preview'
                  className='size-full object-cover'
                />
              </div>
            ) : (
              <div className='mb-3 flex h-48 w-full items-center justify-center rounded-md border border-dashed'>
                <div className='text-center text-muted-foreground'>
                  <ImageIcon className='mx-auto mb-2 size-10 opacity-50' />
                  <p>No costume image</p>
                </div>
              </div>
            )}

            <Button
              type='button'
              className='w-full bg-yellow-300 py-6 text-black hover:bg-yellow-400'
              onClick={() => {
                setUploadDialogOpen(true);
              }}
            >
              <Upload className='mr-2 size-5' />
              Upload Costume Picture
            </Button>
          </div>
        </CardContent>
      </Card>

      <UploadCostumeImage
        actId={actId}
        uploadCostumeDialogOpen={uploadDialogOpen}
        setUploadCostumeDialogOpen={setUploadDialogOpen}
        selectedPerformance={performance}
        setSelectedPerformance={() => null}
      />

      <FormField
        control={control}
        name='costumeName'
        render={({ field }) => (
          <FormItem>
            <FormLabel>Costume Name</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name='costumeDescription'
        render={({ field }) => (
          <FormItem>
            <FormLabel>Costume Description</FormLabel>
            <FormControl>
              <Textarea {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Supplier field removed - now using Costume Company instead */}

      {/* Info Section */}
      <div className='rounded-lg border bg-card p-4'>
        <h3 className='mb-4 text-lg font-semibold'>Info</h3>

        <div className='space-y-4'>
          <div className='flex gap-2'>
            <FormField
              control={control}
              name='costumeCompany'
              render={({ field }) => (
                <FormItem className='flex-1'>
                  <FormLabel>Costume Company</FormLabel>
                  <FormControl>
                    <Input placeholder='e.g., Curtain Call' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name='costumeStyleNumber'
              render={({ field }) => (
                <FormItem className='flex-1'>
                  <FormLabel>Style Number</FormLabel>
                  <FormControl>
                    <Input placeholder='e.g., CC123' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className='flex items-end gap-4'>
            <FormField
              control={control}
              name='costumeColor'
              render={({ field }) => (
                <FormItem className='flex-1'>
                  <FormLabel>Color</FormLabel>
                  <FormControl>
                    <Input placeholder='e.g., Royal Blue' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      </div>

      {/* Sizing Section */}
      <div className='rounded-lg border bg-card p-4'>
        <h3 className='mb-4 text-lg font-semibold'>Sizing</h3>

        <div className='space-y-4'>
          <div className='flex gap-2'>
            <FormField
              control={control}
              name='tightsSelection'
              render={({ field }) => (
                <FormItem className='flex-1'>
                  <FormLabel>Tights Selection</FormLabel>
                  <FormControl>
                    <Input placeholder='e.g., Tan, Suntan' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name='shoeType'
              render={({ field }) => (
                <FormItem className='flex-1'>
                  <FormLabel>Shoe Type</FormLabel>
                  <FormControl>
                    <Input placeholder='e.g., Black Jazz Shoes' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={control}
            name='hairAndMakeupNotes'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Hair & Makeup Notes</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder='e.g., High bun, red lipstick, glitter'
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* Extras Section */}
      <div className='rounded-lg border bg-card p-4'>
        <h3 className='mb-4 text-lg font-semibold'>Extras</h3>

        <div className='space-y-4'>
          {/* Props Needed */}
          <div className='flex items-center justify-between rounded-lg border bg-background p-4'>
            <div>
              <h4 className='font-medium'>Props Needed</h4>
              <p className='text-sm text-muted-foreground'>
                Does this performance require props?
              </p>
            </div>
            <FormField
              control={control}
              name='propsNeeded'
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          {/* Conditional Props Description Field */}
          {propsNeeded && (
            <FormField
              control={control}
              name='propsDescription'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Props Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder='Describe the props needed for this performance...'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
      </div>

      {/* Student Sizes Section */}
      <div className='mt-4'>
        <StudentSizesSection />
      </div>
    </div>
  );
};
